import React, { useState } from 'react';
import { submitProject } from '../../server/api/hackathon';

const HackathonProjectSubmission: React.FC = () => {
  const [formData, setFormData] = useState({
    teamName: '',
    projectName: '',
    description: '',
    githubRepo: '',
    demoLink: '',
    teamMembers: '',
    challenges: '',
    learnings: '',
  });
  const [showThankYou, setShowThankYou] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await submitProject(formData);
      if (result.success) {
        setShowThankYou(true);
        // Reset form
        setFormData({
          teamName: '',
          projectName: '',
          description: '',
          githubRepo: '',
          demoLink: '',
          teamMembers: '',
          challenges: '',
          learnings: '',
        });
      } else {
        throw new Error('Project submission failed');
      }
    } catch (error) {
      console.error('Error submitting project:', error);
      alert('Failed to submit project. Please try again.');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <section className="py-16 backdrop-blur-sm">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {showThankYou ? (
          <div className="text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight pb-2 text-black font-mono">
              Thank You for Your Submission! 🚀
            </h2>
            <p className="mt-4 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Your project has been successfully submitted.
              <br />
              Our judges will review your submission and get back to you soon!
            </p>
            <button
              onClick={() => setShowThankYou(false)}
              className="px-8 py-4 bg-white text-slate-900 border-2 border-cyan-500 hover:bg-cyan-50 transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-1 font-medium text-lg rounded-lg"
            >
              Submit Another Project
            </button>
          </div>
        ) : (
          <>
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight pb-2 text-black font-mono">
                Project Submission
              </h2>
              <br/>
              <p className="mt-4 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
                Share your hackathon project with us! 
                <br/>
                Fill out the form below to submit your project details.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-12 space-y-8">
              <div className="space-y-8">
                <div className="p-6 border border-slate-200 rounded-lg bg-white/50">
                  <h3 className="text-lg font-medium text-slate-900 mb-4">Project Details</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="teamName" className="block text-sm font-medium text-slate-700 mb-2">
                        Team Name *
                      </label>
                      <input
                        type="text"
                        name="teamName"
                        id="teamName"
                        required
                        value={formData.teamName}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="Enter your team name"
                      />
                    </div>

                    <div>
                      <label htmlFor="projectName" className="block text-sm font-medium text-slate-700 mb-2">
                        Project Name *
                      </label>
                      <input
                        type="text"
                        name="projectName"
                        id="projectName"
                        required
                        value={formData.projectName}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="Enter your project name"
                      />
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-2">
                        Project Description *
                      </label>
                      <textarea
                        name="description"
                        id="description"
                        rows={4}
                        required
                        value={formData.description}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="Describe your project in detail"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 border border-slate-200 rounded-lg bg-white/50">
                  <h3 className="text-lg font-medium text-slate-900 mb-4">Links & Resources</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="githubRepo" className="block text-sm font-medium text-slate-700 mb-2">
                        GitHub Repository URL *
                      </label>
                      <input
                        type="url"
                        name="githubRepo"
                        id="githubRepo"
                        required
                        value={formData.githubRepo}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="https://github.com/username/repo"
                      />
                    </div>

                    <div>
                      <label htmlFor="demoLink" className="block text-sm font-medium text-slate-700 mb-2">
                        Demo Link *
                      </label>
                      <input
                        type="url"
                        name="demoLink"
                        id="demoLink"
                        required
                        value={formData.demoLink}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="https://your-demo-url.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 border border-slate-200 rounded-lg bg-white/50">
                  <h3 className="text-lg font-medium text-slate-900 mb-4">Team Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="teamMembers" className="block text-sm font-medium text-slate-700 mb-2">
                        Team Members (comma-separated) *
                      </label>
                      <input
                        type="text"
                        name="teamMembers"
                        id="teamMembers"
                        required
                        value={formData.teamMembers}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="John Doe, Jane Smith"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 border border-slate-200 rounded-lg bg-white/50">
                  <h3 className="text-lg font-medium text-slate-900 mb-4">Project Experience</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="challenges" className="block text-sm font-medium text-slate-700 mb-2">
                        Challenges Faced *
                      </label>
                      <textarea
                        name="challenges"
                        id="challenges"
                        rows={3}
                        required
                        value={formData.challenges}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="Describe the challenges you encountered during development"
                      />
                    </div>

                    <div>
                      <label htmlFor="learnings" className="block text-sm font-medium text-slate-700 mb-2">
                        Key Learnings *
                      </label>
                      <textarea
                        name="learnings"
                        id="learnings"
                        rows={3}
                        required
                        value={formData.learnings}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-lg border-slate-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 bg-white px-4 py-3 text-base"
                        placeholder="Share what you learned from this project"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-4 pt-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-200 w-full">
                  <p className="text-slate-600">
                    Enjoying the hackathon? Consider starring our repository ⭐️
                    <a 
                      href="https://github.com/StructuredLabs/preswald" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 ml-1"
                    >
                      github.com/StructuredLabs/preswald
                    </a>
                  </p>
                </div>
                <button
                  type="submit"
                  className="px-8 py-4 bg-white text-slate-900 border-2 border-cyan-500 hover:bg-cyan-50 transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-1 font-medium text-lg rounded-lg"
                >
                  Submit Project
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
};

export default HackathonProjectSubmission; 